import React, { useState, useEffect } from 'react';
import { X, Database, Braces, AlertCircle, Sparkles, Loader2 } from 'lucide-react';
import { analyticsHubService } from '../../../../../components/api/analyticsHubService';
import type { Theme, ThemeColors, EntityFormData, ReportEntity } from './types';
import { validateEntityJson } from './utils';

const THEME_CONFIG: Record<Theme, ThemeColors> = {
  dark: {
    primary: 'bg-neutral-900',
    secondary: 'bg-neutral-800',
    background: 'bg-neutral-950',
    surface: 'bg-neutral-800',
    border: 'border-neutral-700',
    text: { primary: 'text-white', secondary: 'text-neutral-300', muted: 'text-neutral-400' },
    accent: 'bg-indigo-600',
    hover: 'hover:bg-neutral-700',
    input: 'bg-neutral-700 border-neutral-600 text-white placeholder-neutral-500',
  },
  light: {
    primary: 'bg-white',
    secondary: 'bg-gray-50',
    background: 'bg-gray-100',
    surface: 'bg-white',
    border: 'border-gray-200',
    text: { primary: 'text-gray-900', secondary: 'text-gray-700', muted: 'text-gray-500' },
    accent: 'bg-indigo-600',
    hover: 'hover:bg-gray-100',
    input: 'bg-white border-gray-300 text-gray-900 placeholder-gray-400',
  },
};

const SAMPLE_JSON = `{
  "title": "Sample Report",
  "metrics": {
    "totalSessions": 1240,
    "avgDuration": "4m 32s",
    "satisfaction": 4.7
  },
  "period": "Last 30 days"
}`;

interface EntityModalProps {
  theme?: Theme;
  entity?: ReportEntity | null;
  collectionId: string;
  onClose: () => void;
  onSaved: () => void;
}

const EntityModal: React.FC<EntityModalProps> = ({
  theme = 'dark',
  entity,
  collectionId,
  onClose,
  onSaved,
}) => {
  const colors = THEME_CONFIG[theme];
  const isEdit = !!entity;

  const [form, setForm] = useState<EntityFormData>({
    name: entity?.name ?? '',
    jsonValue: entity ? JSON.stringify(entity.data, null, 2) : SAMPLE_JSON,
  });
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose]);

  const handleSave = async () => {
    const trimmedName = form.name.trim();
    if (!trimmedName) {
      setError('Entity name is required');
      return;
    }
    if (!/^[a-zA-Z][a-zA-Z0-9_]*$/.test(trimmedName)) {
      setError('Name must start with a letter and contain only letters, numbers, and underscores');
      return;
    }

    const result = validateEntityJson(form.jsonValue);
    if (!result.valid) {
      setError(result.error ?? 'Invalid JSON');
      return;
    }

    const tenantId = localStorage.getItem('tenant_id');
    if (!tenantId) {
      setError('Tenant not found. Please sign in again.');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const now = new Date().toISOString();
      const userId = localStorage.getItem('user_uid') ?? undefined;

      await analyticsHubService.upsertEntity({
        _id: entity?.id,
        tenant_id: tenantId,
        entity_name: trimmedName,
        entity_json: result.data!,
        analytics_collection_id: collectionId,
        createdby: isEdit ? undefined : userId,
        createdtime: isEdit ? undefined : now,
        updated_by: userId,
        updated_time: now,
      });

      onSaved();
      onClose();
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to save entity. Please try again.';
      setError(message);
    } finally {
      setSaving(false);
    }
  };

  const loadSample = () => {
    setForm((prev) => ({ ...prev, jsonValue: SAMPLE_JSON }));
    setError(null);
  };

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className={`w-full max-w-md rounded-lg border ${colors.border} ${colors.surface} shadow-2xl overflow-hidden`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={`px-4 py-2.5 border-b ${colors.border} flex items-center justify-between`}>
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-md bg-indigo-600/20 flex items-center justify-center">
              <Database className="w-3.5 h-3.5 text-indigo-400" />
            </div>
            <div>
              <h3 className={`text-xs font-semibold ${colors.text.primary}`}>
                {isEdit ? 'Edit Entity' : 'New Entity'}
              </h3>
              <p className={`text-[10px] ${colors.text.muted}`}>JSON data for template bindings</p>
            </div>
          </div>
          <button type="button" onClick={onClose} className={`p-1 rounded ${colors.hover} ${colors.text.muted}`}>
            <X className="w-3.5 h-3.5" />
          </button>
        </div>

        <div className="p-4 space-y-3">
          <div>
            <label className={`block text-[10px] font-medium uppercase tracking-wide ${colors.text.muted} mb-1`}>
              Entity Name
            </label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => { setForm((prev) => ({ ...prev, name: e.target.value })); setError(null); }}
              placeholder="e.g. conversationStats"
              className={`w-full px-2.5 py-1.5 rounded-md border text-[11px] focus:outline-none focus:ring-1 focus:ring-indigo-500/50 ${colors.input}`}
            />
            <p className={`text-[10px] ${colors.text.muted} mt-0.5`}>
              Template: <code className="text-indigo-400">{`{${form.name || 'entityName'}.field}`}</code>
            </p>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <label className={`text-[10px] font-medium uppercase tracking-wide ${colors.text.muted} flex items-center gap-1`}>
                <Braces className="w-3 h-3" /> JSON Data
              </label>
              <button type="button" onClick={loadSample} className="text-[10px] text-indigo-400 hover:text-indigo-300 flex items-center gap-0.5">
                <Sparkles className="w-2.5 h-2.5" /> Sample
              </button>
            </div>
            <textarea
              value={form.jsonValue}
              onChange={(e) => { setForm((prev) => ({ ...prev, jsonValue: e.target.value })); setError(null); }}
              rows={10}
              spellCheck={false}
              className={`w-full px-2.5 py-1.5 rounded-md border text-[11px] font-mono focus:outline-none focus:ring-1 focus:ring-indigo-500/50 resize-none ${colors.input}`}
            />
          </div>

          {error && (
            <div className="flex items-start gap-1.5 px-2.5 py-1.5 rounded-md bg-red-500/10 border border-red-500/30">
              <AlertCircle className="w-3 h-3 text-red-400 flex-shrink-0 mt-0.5" />
              <p className="text-[11px] text-red-400">{error}</p>
            </div>
          )}
        </div>

        <div className={`px-4 py-2.5 border-t ${colors.border} flex justify-end gap-1.5`}>
          <button type="button" onClick={onClose} disabled={saving} className={`px-3 py-1.5 rounded-md text-[11px] ${colors.text.secondary} ${colors.hover} disabled:opacity-50`}>
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="px-3 py-1.5 rounded-md text-[11px] bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 text-white font-medium flex items-center gap-1.5"
          >
            {saving && <Loader2 className="w-3 h-3 animate-spin" />}
            {isEdit ? 'Update' : 'Create'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EntityModal;
